// Map key-bar body — for Q1 features (orderable key × aggregable value),
// e.g. "score per round" or "minutes per hour-of-day". X-axis is the
// numeric key (its natural ordering is the unlock — no other map chart
// uses it); y-axis is the mean of the value half per key bucket, drawn
// as a vertical bar. Bars beat a line here because the keys are discrete
// integers — connecting them with a line implies smooth interpolation
// between, say, round 3 and round 4, which the data doesn't claim. Line
// charts stay reserved for time series.

import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { api } from '../../api/client';
import type { ChartBodyProps } from '../types';
import { useTimeRangeState, windowForAnchor } from '../windowRange';
import ChartTimeRangePicker from '../ChartTimeRangePicker';
import { aggregateMapKeySeries, type MapValueRow } from './mapAggregation';
import {
  BAR_FILL, BAR_TICK, BAR_TOOLTIP_CURSOR, BarTooltipCard, barChartMargin,
} from './barChartStyles';

function formatKey(k: number): string {
  return Number.isInteger(k) ? String(k) : k.toFixed(2);
}

const MapKeyBarBody: React.FC<ChartBodyProps> = ({ featureId, unit, config, fullscreen }) => {
  const [rows, setRows] = useState<MapValueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get<MapValueRow[]>(`/api/features/values/${featureId}`)
      .then((resp) => { if (!cancelled) { setRows(resp.data); setError(null); } })
      .catch((err) => {
        console.error('Failed to fetch feature values:', err);
        if (!cancelled) setError('Failed to load chart data');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [featureId]);

  // Feed only the rows we can plot (both halves numeric) to the
  // time-range machinery so granularity heuristics aren't biased by
  // malformed pairs the LLM might have emitted.
  const usableRows = useMemo(
    () => rows.filter((r) => r.key_decimal_value != null && r.decimal_value != null),
    [rows],
  );
  const dataDates = useMemo(
    () => usableRows.map((r) => new Date(r.date).getTime()),
    [usableRows],
  );
  const range = useTimeRangeState(dataDates);
  const win = useMemo(
    () => windowForAnchor(range.granularity, range.anchor),
    [range.granularity, range.anchor],
  );

  const series = useMemo(() => aggregateMapKeySeries(rows, win), [rows, win]);

  // When the value half is a labeled rating, spell out the labels on the
  // y-axis instead of bare 1..N numerals — that's the whole point of the
  // labels. For plain `rating` we keep numeric ticks (no labels to show);
  // for free numeric (`decimal`, `num`, `int`) recharts picks ticks
  // automatically as before.
  const valueCfg = config?.params?.value;
  const keyUnit = config?.params?.key?.params?.unit;
  const valueLabels = valueCfg?.type === 'labeled_rating'
    ? valueCfg.params?.labels
    : undefined;
  const ratingMax = (valueCfg?.type === 'rating' || valueCfg?.type === 'labeled_rating')
    ? (valueCfg.params?.max ?? valueLabels?.length ?? 5)
    : undefined;
  const yTicks = ratingMax
    ? Array.from({ length: ratingMax }, (_, i) => i + 1)
    : undefined;
  // Domain starts at 0 so bar heights read correctly (a bar's length is
  // its avg, not avg − 1) while the ticks still label the legal rating
  // range 1..N.
  const yDomain = ratingMax ? ([0, ratingMax] as [number, number]) : undefined;
  const yTickFormatter = valueLabels
    ? (v: number) => valueLabels[v - 1] ?? String(v)
    : undefined;
  // Rough width budget for tick text — recharts won't grow the axis to
  // fit, so leave room for the longest label up to a cap.
  const yWidth = valueLabels
    ? Math.min(140, 16 + 7 * Math.max(0, ...valueLabels.map((l) => l.length)))
    : 50;

  if (loading) {
    return (
      <div className={`flex justify-center items-center ${fullscreen ? 'h-full' : 'h-64'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-8 text-rose-500"><p>{error}</p></div>;
  }
  if (usableRows.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No numeric pairs recorded yet for this feature.</p>
      </div>
    );
  }

  return (
    <div className={fullscreen ? 'h-full flex flex-col' : 'flex flex-col'} style={{ touchAction: 'none' }}>
      <ChartTimeRangePicker state={range} />
      <div className={fullscreen ? 'flex-1 min-h-0' : 'h-80'}>
        {series.length === 0 ? (
          <div className="flex items-center justify-center h-full text-center text-gray-500">
            <p>No numeric pairs in this period.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={barChartMargin(!!fullscreen, 'numeric')}>
              <XAxis
                type="number"
                dataKey="key"
                domain={['dataMin', 'dataMax']}
                padding={{ left: 16, right: 16 }}
                tickFormatter={formatKey}
                tick={BAR_TICK}
                allowDecimals={false}
                tickLine={false}
              />
              <YAxis
                tick={BAR_TICK}
                tickLine={false}
                domain={yDomain}
                ticks={yTicks}
                tickFormatter={yTickFormatter}
                width={yWidth}
              />
              <Tooltip
                cursor={BAR_TOOLTIP_CURSOR}
                content={({ active, payload }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const p = payload[0].payload as { key: number; avg: number; count: number };
                  const avgFmt = Number.isInteger(p.avg) ? String(p.avg) : p.avg.toFixed(2);
                  // For labeled_rating, also show the label nearest to
                  // the average — the y-axis labels already give context
                  // but the bar top usually lands between ticks, so the
                  // rounded label keeps the tooltip self-contained.
                  const nearestLabel = valueLabels
                    ? valueLabels[Math.min(Math.max(Math.round(p.avg) - 1, 0), valueLabels.length - 1)]
                    : null;
                  return (
                    <BarTooltipCard>
                      <div className="text-gray-600">key: {keyUnit ? `${formatKey(p.key)} ${keyUnit}` : formatKey(p.key)}</div>
                      <div className="text-gray-900">
                        avg: {unit ? `${avgFmt} ${unit}` : avgFmt}
                        {nearestLabel ? ` ≈ ${nearestLabel}` : ''}
                      </div>
                      <div className="text-gray-500 text-xs">{p.count} checkin{p.count === 1 ? '' : 's'}</div>
                    </BarTooltipCard>
                  );
                }}
              />
              <Bar dataKey="avg" fill={BAR_FILL} isAnimationActive={false} maxBarSize={24} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default MapKeyBarBody;
